import { Combobox } from '@headlessui/react'
import clsx from 'clsx'
import { useCallback, useContext, useState } from 'react'
import { useTranslation } from 'react-i18next'
import api from '../../../api'
import { ProjectContext } from '../../../contexts'
import { useResolver } from '../../../hooks'
import { List } from '../../../types'
import { ButtonGroup } from '../../../ui'
import { SingleSelect } from '../../../ui/form/SingleSelect'
import { ChevronUpDownIcon } from '../../../ui/icons'
import { highlightSearchNodes, usePopperSelectDropdown } from '../../../ui/utils'
import { getOperatorTypes, RuleEditProps } from './RuleHelpers'

export default function ListRuleEdit({
    rule,
    setRule,
    controls,
}: Omit<RuleEditProps, 'root' | 'headerPrefix' | 'depth'>) {
    const { t } = useTranslation()
    const [{ id: projectId }] = useContext(ProjectContext)
    const {
        setReferenceElement,
        setPopperElement,
        attributes,
        styles,
    } = usePopperSelectDropdown()

    const listId = Number(rule.value) || 0
    const [list] = useResolver(useCallback(async () => listId ? await api.lists.get(projectId, listId) : null, [projectId, listId]))
    const [query, setQuery] = useState<string>()
    const [result] = useResolver(useCallback(async () => await api.lists.search(projectId, {
        q: query ?? '',
        limit: 50,
        filter: { type: ['static', 'synced'], all_folders: true },
    }), [projectId, query]))

    return (
        <div className="rule">
            <ButtonGroup className="ui-select">
                <SingleSelect
                    value={rule.operator}
                    onChange={operator => setRule({ ...rule, operator })}
                    options={getOperatorTypes(t).list}
                    required
                    hideLabel
                    size="small"
                    toValue={x => x.key}
                />
                <Combobox onChange={(selected: List | null) => {
                    if (!selected) return
                    setRule({ ...rule, value: String(selected.id) })
                    setQuery(undefined)
                }}>
                    <span className="ui-text-input">
                        <Combobox.Input
                            value={query ?? list?.name ?? ''}
                            onChange={e => setQuery(e.target.value)}
                            placeholder={t('rule_list_placeholder')}
                            required
                            ref={setReferenceElement}
                            className="small"
                        />
                    </span>
                    <Combobox.Button className="ui-button small secondary">
                        <ChevronUpDownIcon />
                    </Combobox.Button>
                    <Combobox.Options
                        className="select-options nowheel"
                        ref={setPopperElement}
                        style={styles.popper}
                        {...attributes.popper}
                    >
                        {
                            result?.results.length
                                ? result.results.map(option => (
                                    <Combobox.Option
                                        key={option.id}
                                        value={option}
                                        className={({ focus, selected }) => clsx('select-option', focus && 'active', selected && 'selected')}
                                    >
                                        <span>
                                            {highlightSearchNodes(option.name, query ?? '')}
                                        </span>
                                    </Combobox.Option>
                                ))
                                : <li className="select-option-empty">No Results</li>
                        }
                    </Combobox.Options>
                </Combobox>
                {controls}
            </ButtonGroup>
        </div>
    )
}
